// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { ApiUser } from '../../types/accounts';

interface FriendsTabProps {
  account: ApiUser;
}

interface Friend {
  user: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string;
    online: boolean;
  };
  state: number; // 0 = no relationship, 1 = friend, 2 = invite sent, 3 = invite received, 4 = blocked
  update_time: string;
}

const FriendsTab: React.FC<FriendsTabProps> = ({ account }) => {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadFriends();
  }, [account.id]);

  const loadFriends = async () => {
    setLoading(true);
    setError('');

    try {
      // This would need to be implemented in the console service
      // const friendsList = await consoleService.listFriends('', account.id);
      // setFriends(friendsList.friends || []);
      
      // For now, simulate loading
      setTimeout(() => {
        setFriends([]);
        setLoading(false);
      }, 1000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load friends');
      setLoading(false);
    }
  };

  const getStateLabel = (state: number) => {
    switch (state) {
      case 1: return 'Friend';
      case 2: return 'Invite Sent';
      case 3: return 'Invite Received';
      case 4: return 'Blocked';
      default: return 'No Relationship';
    }
  };

  const getStateClass = (state: number) => {
    switch (state) {
      case 1: return 'badge-success';
      case 2: return 'badge-warning';
      case 3: return 'badge-info';
      case 4: return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="friends-tab">
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <strong>Loading friends...</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="friends-tab">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Friends & Relationships</h5>
        <span className="text-muted">{friends.length} relationships found</span>
      </div>

      {error && (
        <div className="alert alert-danger">
          <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="me-2" />
          <strong>Error loading friends:</strong> {error}
        </div>
      )}

      {friends.length === 0 && !error ? (
        <div className="alert alert-info">
          <strong>No friends or relationships found.</strong>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Username</th>
                <th>Display Name</th>
                <th>Status</th>
                <th>Relationship</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {friends.map((friend, index) => (
                <tr key={friend.user.id}>
                  <td>
                    <code>{friend.user.id}</code>
                  </td>
                  <td>{friend.user.username}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      {friend.user.avatar_url && (
                        <img
                          src={friend.user.avatar_url}
                          alt=""
                          className="rounded-circle mr-2"
                          width="24"
                          height="24"
                        />
                      )}
                      {friend.user.display_name}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${friend.user.online ? 'badge-success' : 'badge-secondary'}`}>
                      {friend.user.online ? 'Online' : 'Offline'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getStateClass(friend.state)}`}>
                      {getStateLabel(friend.state)}
                    </span>
                  </td>
                  <td>{formatDate(friend.update_time)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FriendsTab;
